import http, { request } from "@config/http";
import { Page, Pagination, List } from "@services/common/model/page.model";
import {
  JobModel,
  JobQueryModel,
  AntibodyAntigenModel,
  JobBaseModel,
  QuotaModel,
  AlphafoldFileModel,
  FileUploadModel,
  PdbInfoModel,
  DeNovoAntibodyDesignModel,
  CheckResidueModel,
  SeqExtractDomainModel,
  PendingNumModel,
} from "@services/task/model/task.model";
import { JobTypeEnum, QueueIdentityEnum } from "./enum/task.enum";
import { IdNameModel } from "@services/common/model/gloabal.model";

export const getJobListApi = async (params: JobQueryModel) => {
  const { data } = await http.get<null, Page<Pagination<JobModel>>>(
    `lite/job_base/list_by_type`,
    {
      params,
    }
  );

  return data;
};

export const deleteJobApi = async (jids: string[]) => {
  return http.delete<null, Page<null>>(`lite/job_base/delete`, {
    data: {
      jids,
    },
  });
};

export const createDockingApi = async (params: AntibodyAntigenModel) => {
  return http.post<null, Page<null>>(`lite/job/dock/create`, {
    ...params,
  });
};

export const checkJobNameApi = async (job_name: string) => {
  return http.post<null, Page<null>>(`lite/job_base/check_name`, {
    job_name,
  });
};

export const getJobDetailApi = async (job_id: string) => {
  return http.get<null, Page<JobBaseModel>>(
    `lite/job_base/base_info/${job_id}`,
    {}
  );
};

export const getSummaryCsvApi = async (fids: string[]) => {
  return request.post<null, string>(`lite/job_base/read_summary_csv`, {
    fids,
  });
};

export const getInfoByTypeApi = async (type_name: JobTypeEnum) => {
  return http.get<null, Page<QuotaModel>>(`lite/job_base/info_by_type`, {
    params: {
      type_name,
    },
  });
};

export const downloadPdbFileApi = async (id: string) => {
  try {
    const fileName = id.toLowerCase();
    const response = await fetch(
      `https://www.ebi.ac.uk/pdbe/static/entry/${fileName}_updated.cif`
    );
    // `https://files.rcsb.org/download/${fileName}.cif`

    if (response.status !== 200) {
      return null;
    }

    const blob = await response.blob();
    const file = new File([blob], `${fileName}.cif`);

    return file;
  } catch (e) {
    return null;
    // throw new Error("File not found");
  }
};

export const downloadAlphafoldApi = async (id: string) => {
  try {
    const [source] = await request.get<null, AlphafoldFileModel[]>(
      `https://www.alphafold.ebi.ac.uk/api/prediction/${id}`,
      {}
    );

    const response = await fetch(`${source.cifUrl}`);

    if (response.status !== 200) {
      return null;
    }

    const blob = await response.blob();
    const file = new File([blob], `${source.entryId}.cif`);

    return file;
  } catch (e) {
    return null;
  }
};

export const uploadFileApi = async (formData: FormData) => {
  return http.post<null, Page<FileUploadModel>>(`lite/file/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "Content-Encoding": "gzip",
    },
  });
};

export const getBatchPdbInfoApi = async (
  fileIds: string[],
  dataTypes: string[]
) => {
  const { data } = await http.post<null, Page<List<PdbInfoModel>>>(
    `lite/util/batch_get_pdb_info`,
    {
      file_ids: fileIds,
      data_types: dataTypes,
    }
  );

  return data.list;
};

export const downloadJobFilesApi = async (jids: string[]) => {
  return request.post<null, string>(
    `lite/job_base/download_job_files`,

    {
      jids,
    },
    {
      responseType: "blob",
    }
  );
};

export const createDeNovoAntibodyDesignJobApi = async (
  params: DeNovoAntibodyDesignModel
) => {
  return http.post<null, Page<null>>(`lite/job/crd_h3_design/create`, {
    ...params,
  });
};

export const checkPdbApi = (params: CheckResidueModel) => {
  return request.post<null, Page<null>>(
    "lite/job/crd_h3_design/check_hotspots",
    {
      ...params,
    }
  );
};

export const getExamplesApi = async (type_name: JobTypeEnum) => {
  return http.get<null, Page<List<IdNameModel>>>(`lite/job_base/examples`, {
    params: {
      type_name,
    },
  });
};

export const getSeqExtractDomainApi = (sequence: string) => {
  return http.post<null, Page<SeqExtractDomainModel>>(
    "lite/util/seq_extract_v_domain",
    {
      sequence,
    }
  );
};

export const getPendingNumberApi = async (
  queue_identity: QueueIdentityEnum,
  signal?: AbortSignal
) => {
  const data = await http.get<null, Page<PendingNumModel>>(
    `lite/job_base/pending_num`,
    {
      params: {
        queue_identity,
      },
      signal,
    }
  );

  return data.data;
};
